import { useState } from 'react';
import { reportsAPI } from '../lib/api';
import { downloadBlob } from '../lib/utils';
import { BarChart3, Download, FileSpreadsheet, FileText, Users, Clock, Scale, TrendingDown, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const reportTypes = [
  { id: 'employees', label: 'Active Employees', icon: Users, description: 'List of all active employees with details' },
  { id: 'leave', label: 'Leave Summary', icon: Clock, description: 'Leave summary by department' },
  { id: 'disciplinary', label: 'Disciplinary Summary', icon: Scale, description: 'Summary of disciplinary cases' },
  { id: 'turnover', label: 'Employee Turnover', icon: TrendingDown, description: 'Turnover rate and terminated employees' },
];

export default function Reports() {
  const [selectedReport, setSelectedReport] = useState('employees');
  const [format, setFormat] = useState('excel');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [turnoverData, setTurnoverData] = useState(null);

  const generateReport = async () => {
    setLoading(true);
    try {
      if (selectedReport === 'employees') {
        if (format === 'excel') {
          const response = await reportsAPI.getEmployeesExcel({});
          downloadBlob(response.data, 'employees_report.xlsx');
          toast.success('Report downloaded');
        } else if (format === 'pdf') {
          const response = await reportsAPI.getEmployeesPdf({});
          downloadBlob(response.data, 'employees_report.pdf');
          toast.success('Report downloaded');
        } else {
          const response = await reportsAPI.getEmployees({});
          setReportData(response.data);
        }
      } else if (selectedReport === 'leave') {
        if (format === 'excel') {
          const response = await reportsAPI.getLeaveSummaryExcel({});
          downloadBlob(response.data, 'leave_summary.xlsx');
          toast.success('Report downloaded');
        } else {
          const response = await reportsAPI.getLeaveSummary({});
          setReportData(response.data);
        }
      } else if (selectedReport === 'disciplinary') {
        const response = await reportsAPI.getDisciplinarySummary({});
        setReportData(response.data);
      } else if (selectedReport === 'turnover') {
        const response = await reportsAPI.getTurnover();
        setTurnoverData(response.data);
      }
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 page-transition" data-testid="reports-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-3">
          <BarChart3 className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
          Reports
        </h1>
        <p className="text-muted-foreground mt-1">Generate and export HR reports</p>
      </div>

      {/* Report Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {reportTypes.map((report) => (
          <button
            key={report.id}
            onClick={() => setSelectedReport(report.id)}
            data-testid={`report-type-${report.id}`}
            className={`stat-tile text-left transition-all ${
              selectedReport === report.id 
                ? 'ring-2 ring-primary bg-primary/10' 
                : ''
            }`}
          >
            <report.icon className={`w-8 h-8 mb-3 ${
              selectedReport === report.id ? 'text-primary' : 'text-muted-foreground'
            }`} />
            <p className="font-medium text-foreground">{report.label}</p>
            <p className="text-sm text-muted-foreground mt-1">{report.description}</p>
          </button>
        ))}
      </div>

      {/* Export Options */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold mb-4">Export Options</h3>
        <div className="flex flex-col sm:flex-row gap-4">
          {selectedReport !== 'turnover' && selectedReport !== 'disciplinary' && (
            <Select value={format} onValueChange={setFormat}>
              <SelectTrigger className="w-full sm:w-48" data-testid="select-format">
                <SelectValue placeholder="Select format" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="excel">
                  <div className="flex items-center gap-2">
                    <FileSpreadsheet className="w-4 h-4" />
                    Excel (.xlsx)
                  </div>
                </SelectItem>
                <SelectItem value="pdf">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    PDF
                  </div>
                </SelectItem>
                <SelectItem value="json">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    View Data
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          )}
          
          <Button onClick={generateReport} disabled={loading} data-testid="generate-report-btn">
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Generate Report
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Turnover Report Display */}
      {turnoverData && selectedReport === 'turnover' && (
        <div className="glass-card p-6">
          <h3 className="text-lg font-semibold mb-4">Turnover Report</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-6 rounded-xl bg-secondary/30 text-center">
              <p className="text-4xl font-bold text-primary">{turnoverData.total_active}</p>
              <p className="text-muted-foreground mt-2">Active Employees</p>
            </div>
            <div className="p-6 rounded-xl bg-secondary/30 text-center">
              <p className="text-4xl font-bold text-red-400">{turnoverData.terminated_last_90_days}</p>
              <p className="text-muted-foreground mt-2">Terminated (90 days)</p>
            </div>
            <div className="p-6 rounded-xl bg-secondary/30 text-center">
              <p className="text-4xl font-bold text-amber-400">{turnoverData.turnover_rate}%</p>
              <p className="text-muted-foreground mt-2">Turnover Rate</p>
            </div>
          </div>
        </div>
      )}

      {/* Data Preview */}
      {reportData && (format === 'json' || selectedReport === 'disciplinary') && (
        <div className="glass-card p-6">
          <h3 className="text-lg font-semibold mb-4">Report Data</h3>
          <div className="bg-secondary/30 rounded-lg p-4 overflow-auto max-h-96">
            <pre className="text-sm text-muted-foreground">
              {JSON.stringify(reportData, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
